<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { ClipboardPaste } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from "@/components/ui/alert-dialog";
  import { Textarea } from "@/components/ui/textarea";
  import { isEmpty } from "@utils/toolBox";
  import { useSurveyStatusStore } from "@stores/survey/setup/surveyStatus";

  const surveyStatusStore = useSurveyStatusStore();
  const { currentStatus } = storeToRefs(surveyStatusStore);

  const props = withDefaults(
    defineProps<{
      fieldName: string;
      fieldTitles: string[];
      disabled?: boolean;
      currentCard?: object;
      unrestricted?: boolean;
      size?: string;
    }>(),
    { currentCard: undefined, size: undefined },
  );

  const emit = defineEmits<{
    bulkAdd: [elements: string[] | undefined];
  }>();

  const dialogOpen = ref(false);
  const textareaValue = ref("");

  const isBulkAddRestricted = computed(() => !props.unrestricted && !["draft", "review"].includes(currentStatus.value));

  const dialogDescription = computed(() => {
    if (isBulkAddRestricted.value) {
      return "Bulk add is only available when the survey is in draft or review status.";
    }
    return `Please paste in a list of ${props.fieldName}`;
  });
  // Initialize textarea with existing field titles when dialog opens
  watch(dialogOpen, (isOpen) => {
    if (isOpen) {
      textareaValue.value = props.fieldTitles?.join("\n") ?? "";
    }
  });

  const handleConfirm = () => {
    const cleanedElements = cleanElements(textareaValue.value);
    emit("bulkAdd", cleanedElements);
    dialogOpen.value = false;
    textareaValue.value = "";
  };

  const cleanElements = (value: string) => {
    if (isEmpty(value)) return;
    const splitedElements = value.split(/\n+/);
    const cleanedElements = splitedElements.map((el) => el.trim()).filter((el) => !isEmpty(el));
    return cleanedElements;
  };
</script>

<template>
  <Button
    variant="outline"
    :size="props.size"
    data-cy="bulk-add-button"
    @click="dialogOpen = true">
    <ClipboardPaste class="size-3.5" />
    Paste list
  </Button>

  <AlertDialog v-model:open="dialogOpen">
    <AlertDialogContent class="bulk-add">
      <AlertDialogHeader>
        <AlertDialogTitle>Paste a list of {{ fieldName }}</AlertDialogTitle>
        <AlertDialogDescription> {{ dialogDescription }}</AlertDialogDescription>
      </AlertDialogHeader>
      <Textarea
        v-model="textareaValue"
        :placeholder="`Paste your ${fieldName} here...`"
        class="min-h-[150px] max-h-[50vh]"
        data-cy="textarea-bulk-add" />
      <AlertDialogFooter>
        <AlertDialogCancel @click="dialogOpen = false">Cancel</AlertDialogCancel>
        <AlertDialogAction
          :disabled="disabled || isBulkAddRestricted"
          @click="handleConfirm">
          OK
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
